import { baseURL } from './axios/envConfig'
import { endpoints } from './axios/endpoints'
import { errorMessage } from './axios/errorMessage'
import { handleUnauthorized } from '../helpers/session'
import { parseSseBuffer, shortLinkResultFrom } from '../helpers/shortLinks'
import { toCSV } from '../utils/csv'

/** A short link failure with the text to show. */
export class ShortLinkError extends Error {
    /**
     * @param {string} message what to show the user
     * @param {{unauthorized?: boolean}} [details] `unauthorized` when the
     *     session ended (the user is already being sent to sign in)
     */
    constructor(message, { unauthorized = false } = {}) {
        super(message)
        this.name = 'ShortLinkError'
        this.unauthorized = unauthorized
    }
}

/** The response body as JSON, else its text, else null. */
const readBody = async (response) => {
    try {
        const text = await response.text()
        try {
            return JSON.parse(text)
        } catch {
            return text
        }
    } catch {
        return null
    }
}

/**
 * Act on one stream event: report progress (null), return the result of a
 * `complete` event, or throw the message of an `error` event.
 */
const outcomeOf = (event, onProgress) => {
    if (event?.type === 'progress') {
        onProgress?.({
            processed: Number(event.processed) || 0,
            total: Number(event.total) || 0,
        })
        return null
    }
    if (event?.type === 'complete') return shortLinkResultFrom(event)
    if (event?.type === 'error')
        throw new ShortLinkError(
            `Short links were not made: ${
                event.message || 'the short link service failed.'
            }`
        )
    return null
}

/**
 * Replace the links in `columns` of every row with short links, through the
 * streaming route, reporting progress as it goes.
 *
 * The upload is built from the PARSED rows (`toCSV(rows, headers)`), not the
 * original file: the server then reads the same trimmed headers the page
 * does (the old app sent the raw file, so a header with spaces or a
 * byte-order mark failed or dropped out of the send), and a list handed over
 * from another page, which has no file, works too.
 *
 * `fetch` does not pass through the axios interceptors, so a 401 is handled
 * here (sign-in redirect) and every failure is a ShortLinkError carrying the
 * text to show.
 *
 * @param {object} request
 * @param {object[]} request.rows the parsed rows
 * @param {string[]} request.headers their columns, in order
 * @param {string[]} request.columns the columns holding links
 * @param {(progress: {processed: number, total: number}) => void} [request.onProgress]
 * @param {object} [deps] injectable for tests: `fetchImpl`, `base` (API
 *     base URL), `onUnauthorized`
 * @returns {Promise<{rows: object[], total: number, columns: string[], failedRows: number[]}>}
 */
export const generateShortLinks = async (
    { rows, headers, columns, onProgress },
    {
        fetchImpl = (...args) => window.fetch(...args),
        base = baseURL,
        onUnauthorized = handleUnauthorized,
    } = {}
) => {
    const form = new FormData()
    form.append(
        'file',
        new Blob([toCSV(rows, headers)], { type: 'text/csv' }),
        'recipients.csv'
    )
    form.append('columns', JSON.stringify(columns))

    let response
    try {
        response = await fetchImpl(`${base}${endpoints.shortLinksStream}`, {
            method: 'POST',
            body: form,
            credentials: 'include',
        })
    } catch {
        throw new ShortLinkError(
            'Could not reach the short link service. Check your connection and try again.'
        )
    }

    if (response.status === 401) {
        onUnauthorized()
        throw new ShortLinkError(
            'Your session has ended. Sign in again to make short links.',
            { unauthorized: true }
        )
    }
    if (!response.ok || !response.body?.getReader) {
        const data = await readBody(response)
        throw new ShortLinkError(
            `Short links were not made: ${errorMessage({
                status: response.status,
                data,
            })}`
        )
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    for (;;) {
        let chunk
        try {
            chunk = await reader.read()
        } catch {
            throw new ShortLinkError(
                'The connection to the short link service dropped. Try again.'
            )
        }
        if (chunk.done) break
        buffer += decoder.decode(chunk.value, { stream: true })
        const { events, rest } = parseSseBuffer(buffer)
        buffer = rest
        for (const event of events) {
            const result = outcomeOf(event, onProgress)
            if (result) {
                // The server is done; nothing more to read.
                reader.cancel?.()?.catch?.(() => {})
                return result
            }
        }
    }
    // The last event may arrive without its closing blank line.
    for (const event of parseSseBuffer(`${buffer}\n\n`).events) {
        const result = outcomeOf(event, onProgress)
        if (result) return result
    }
    throw new ShortLinkError(
        'The short link service stopped before it finished. Try again.'
    )
}
